import React, { ReactNode } from 'react';
import {
  statusChipClasses,
  medicationOwnershipLabel,
  medicationProvenanceLabel,
  transmissionFailureAlert,
  type StatusTone,
} from '../../../utils/medicationLabels';
import type { ChartMedication } from './medicationTabTypes';

interface MedicationSummaryRowProps {
  medication: {
    medication_name?: string;
    dosage?: string;
    frequency?: string;
    /** Vendor (DoseSpot) medication status synced onto the chart row. */
    rx_status?: string | null;
  };
  statusLabel: string;
  statusTone?: StatusTone;
  badge?: string;
  dateLabel?: string;
  /** Suggestion-only chip (e.g. 'Possible duplicate') — opens a review action, never auto-acts. */
  suggestionChip?: { label: string; onClick: () => void };
  expanded?: boolean;
  onToggle?: () => void;
  children?: ReactNode;
  tone?: 'current' | 'unknown' | 'past';
}

function MedicationSummaryRow({
  medication,
  statusLabel,
  statusTone,
  badge,
  dateLabel,
  suggestionChip,
  expanded = false,
  onToggle,
  children,
  tone,
}: MedicationSummaryRowProps) {
  // A failed vendor transmission must not hide behind an 'Active' chip on the
  // collapsed row. Past sections already surface it via pastMedicationStatusChip.
  const transmissionAlert = tone === 'past' ? undefined : transmissionFailureAlert(medication);
  return (
    <div className="min-w-0 border-b border-gray-200 dark:border-gray-700">
      <div
        className={`flex w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-1 px-0 py-1.5 text-left sm:flex-nowrap sm:gap-2 ${
          onToggle ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''
        }`}
        role={onToggle ? 'button' : undefined}
        tabIndex={onToggle ? 0 : undefined}
        aria-expanded={onToggle ? expanded : undefined}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (onToggle && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onToggle();
          }
        }}
      >
        {/* Below sm the name owns its own line so meta can never squeeze it to zero width (PSYCH-05b). */}
        <span
          className="min-w-0 basis-full break-words font-medium text-gray-900 dark:text-white sm:flex-1 sm:basis-auto sm:truncate"
          title={medication.medication_name || undefined}
        >
          {medication.medication_name || 'Unknown Medication'}
        </span>
        <span className="min-w-0 break-words text-sm text-gray-600 dark:text-gray-300 sm:shrink-0">
          {medication.dosage || 'Dose not recorded'}
        </span>
        {medication.frequency && (
          <span className="min-w-0 break-words text-sm text-gray-600 dark:text-gray-300 sm:shrink-0">
            {medication.frequency}
          </span>
        )}
        {dateLabel && (
          <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
            {dateLabel}
          </span>
        )}
        {badge && (
          <span className="shrink-0 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
            {badge}
          </span>
        )}
        {transmissionAlert && (
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusChipClasses('error')}`}
          >
            {transmissionAlert}
          </span>
        )}
        {suggestionChip && (
          <button
            type="button"
            onClick={(event) => { event.stopPropagation(); suggestionChip.onClick(); }}
            className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300"
          >
            {suggestionChip.label}
          </button>
        )}
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            statusTone ? statusChipClasses(statusTone) : 'text-gray-600 dark:text-gray-300'
          }`}
        >
          {statusLabel}
        </span>
        {onToggle && (
          <span className="shrink-0 text-gray-500" aria-hidden="true">
            {expanded ? '▴' : '▾'}
          </span>
        )}
      </div>
      {expanded && <div className="border-t border-gray-100 py-2 dark:border-gray-600">{children}</div>}
    </div>
  );
}

/** Ownership + provenance chips — two independent facts, never merged. */
export function MedMetaLine({ med }: { med: ChartMedication }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
        {medicationOwnershipLabel(med)}
      </span>
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
        {medicationProvenanceLabel(med)}
      </span>
    </div>
  );
}

export default MedicationSummaryRow;
